// Shared UI primitives for the redesigned pages (Dashboard, Records, Mailboxes, Demos, Collector).
import React, { useState, useCallback, useRef } from 'react';

export const Card = ({ children, style = {}, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: 'var(--c-bg-1)',
      border: '1px solid var(--c-border)',
      borderRadius: 14,
      padding: 20,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'border-color 0.2s',
      ...style,
    }}
    onMouseEnter={onClick ? (e) => { e.currentTarget.style.borderColor = 'var(--c-border-bright)'; } : undefined}
    onMouseLeave={onClick ? (e) => { e.currentTarget.style.borderColor = 'var(--c-border)'; } : undefined}
  >
    {children}
  </div>
);

export const StatCard = ({ icon: Icon, label, value, sub, color = 'var(--c-primary)', onClick }) => (
  <Card onClick={onClick} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, minWidth: 0 }}>
    {Icon && (
      <div style={{
        width: 40, height: 40, borderRadius: 11, flexShrink: 0,
        background: 'var(--c-surface)', border: '1px solid var(--c-border)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color,
      }}>
        <Icon size={19} />
      </div>
    )}
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-text-3)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
      <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 26, fontWeight: 700, color: 'var(--c-text)', lineHeight: 1.25 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--c-text-2)', marginTop: 2 }}>{sub}</div>}
    </div>
  </Card>
);

const CHIP_COLORS = {
  green: ['rgba(0,179,134,0.12)', 'var(--c-primary)'],
  red: ['rgba(239,68,68,0.12)', 'var(--c-red)'],
  yellow: ['rgba(245,158,11,0.14)', 'var(--c-yellow)'],
  blue: ['rgba(59,130,246,0.12)', 'var(--c-blue)'],
  purple: ['rgba(139,92,246,0.12)', 'var(--c-purple)'],
  gray: ['var(--c-surface)', 'var(--c-text-2)'],
};

export const Chip = ({ children, tone = 'gray', style = {} }) => {
  const [bg, fg] = CHIP_COLORS[tone] || CHIP_COLORS.gray;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 999, background: bg, color: fg,
      fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', ...style,
    }}>
      {children}
    </span>
  );
};

export const STATUS_TONE = {
  sent: 'green', pending: 'gray', failed: 'red', bounced: 'red',
  invalid: 'yellow', do_not_contact: 'purple',
};

export const SOURCE_TONE = { google: 'blue', osm: 'yellow' };

export const Btn = ({ children, onClick, tone = 'default', disabled, small, style = {}, title }) => {
  const tones = {
    default: { background: 'var(--c-surface)', color: 'var(--c-text)', border: '1px solid var(--c-border)' },
    primary: { background: 'var(--c-primary)', color: 'var(--c-btn-text)', border: '1px solid transparent' },
    danger: { background: 'rgba(239,68,68,0.1)', color: 'var(--c-red)', border: '1px solid rgba(239,68,68,0.3)' },
  };
  return (
    <button
      onClick={onClick} disabled={disabled} title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        padding: small ? '6px 12px' : '9px 16px', borderRadius: 10,
        fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: small ? 12.5 : 13.5,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s', ...tones[tone], ...style,
      }}
    >
      {children}
    </button>
  );
};

export const inputStyle = {
  padding: '9px 12px', borderRadius: 10, fontSize: 13.5,
  border: '1px solid var(--c-border)', background: 'var(--c-bg)',
  color: 'var(--c-text)', fontFamily: "'Inter', sans-serif", outline: 'none',
};

export const useToasts = () => {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);
  const push = useCallback((message, type = 'success') => {
    const id = ++idRef.current;
    setToasts((t) => [...t, { id, message, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4200);
  }, []);
  const node = (
    <div style={{ position: 'fixed', bottom: 22, right: 22, zIndex: 400, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {toasts.map((t) => (
        <div key={t.id} style={{
          padding: '11px 16px', borderRadius: 12, fontSize: 13, fontWeight: 500,
          background: 'var(--c-bg-1)', color: 'var(--c-text)',
          border: `1px solid ${t.type === 'error' ? 'var(--c-red)' : 'var(--c-primary)'}`,
          boxShadow: 'var(--shadow-elevated)', maxWidth: 380,
        }}>
          {t.message}
        </div>
      ))}
    </div>
  );
  return [push, node];
};

export const Spinner = ({ size = 18 }) => (
  <div className="spinner" style={{ width: size, height: size, borderWidth: 2 }} />
);

export const EmptyState = ({ icon: Icon, title, sub }) => (
  <div style={{ textAlign: 'center', padding: '56px 20px', color: 'var(--c-text-3)' }}>
    {Icon && <Icon size={34} style={{ marginBottom: 12, opacity: 0.5 }} />}
    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--c-text-2)' }}>{title}</div>
    {sub && <div style={{ fontSize: 13, marginTop: 5 }}>{sub}</div>}
  </div>
);

export const ConfirmDialog = ({ open, title, body, confirmLabel = 'Confirm', danger, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300, background: 'var(--c-overlay)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onCancel}>
      <div style={{
        width: 420, maxWidth: '100%', background: 'var(--c-bg)', borderRadius: 16,
        border: '1px solid var(--c-border)', boxShadow: 'var(--shadow-elevated)', padding: 24,
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 17, fontWeight: 700, color: 'var(--c-text)' }}>{title}</div>
        <div style={{ fontSize: 13.5, color: 'var(--c-text-2)', marginTop: 10, lineHeight: 1.55 }}>{body}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <Btn onClick={onCancel}>Cancel</Btn>
          <Btn tone={danger ? 'danger' : 'primary'} onClick={onConfirm}>{confirmLabel}</Btn>
        </div>
      </div>
    </div>
  );
};

export const fmtNum = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-ZA'));

export const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};
